import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  taskPriorityValue,
  type UpdateTaskInput,
  type WorkspaceTask,
} from "@/hooks/useWorkspaceTasks";
import {
  useProjectColumns,
  type WorkspaceProject,
} from "@/hooks/useWorkspaceProjects";

interface LinkTaskToProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: WorkspaceTask | null;
  projects: WorkspaceProject[];
  onSave: (input: UpdateTaskInput) => Promise<void>;
}

export function LinkTaskToProjectDialog({
  open,
  onOpenChange,
  task,
  projects,
  onSave,
}: LinkTaskToProjectDialogProps) {
  const [projectId, setProjectId] = useState<string>("");
  const [columnId, setColumnId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { columns, loading: columnsLoading } = useProjectColumns(projectId || null);

  useEffect(() => {
    if (!open || !task) return;
    const defaultProject = projects[0]?.id ?? "";
    setProjectId(defaultProject);
    setColumnId("");
  }, [open, task, projects]);

  useEffect(() => {
    if (!open) return;
    setColumnId("");
  }, [open, projectId]);

  useEffect(() => {
    if (!open || !projectId || columnsLoading) return;
    if (columns.length === 0) {
      setColumnId("");
      return;
    }
    setColumnId((current) =>
      current && columns.some((c) => c.id === current) ? current : columns[0].id,
    );
  }, [open, projectId, columns, columnsLoading]);

  const handleSubmit = async () => {
    if (!task) return;
    if (!projectId) {
      toast({ title: "Selecione um projeto", variant: "destructive" });
      return;
    }
    if (columns.length > 0 && !columnId) {
      toast({ title: "Selecione uma coluna", variant: "destructive" });
      return;
    }

    try {
      setSubmitting(true);
      await onSave({
        taskId: task.id,
        title: task.title,
        description: task.description,
        assigned_member_id: task.assigned_member_id,
        due_date: task.due_date,
        due_at: task.due_at,
        priority: taskPriorityValue(task),
        lead_id: task.lead_id ?? null,
        tags: task.tags ?? [],
        allowPastDue: true,
        project_id: projectId,
        project_column_id: columnId || null,
      });
      onOpenChange(false);
      toast({
        title: "Tarefa vinculada",
        description: "A tarefa foi movida para o projeto selecionado.",
      });
    } catch (err: unknown) {
      toast({
        title: "Erro ao vincular tarefa",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md min-w-0 overflow-hidden sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Vincular a projeto</DialogTitle>
        </DialogHeader>

        <div className="min-w-0 space-y-4 py-2">
          {task && (
            <p className="min-w-0 break-words text-sm text-muted-foreground">
              Tarefa:{" "}
              <span className="line-clamp-2 font-medium text-foreground">{task.title}</span>
            </p>
          )}

          {projects.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum projeto disponível. Crie um projeto antes de vincular.
            </p>
          ) : (
            <>
              <div className="min-w-0 space-y-2">
                <Label>Projeto</Label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger className="w-full max-w-full">
                    <SelectValue placeholder="Selecionar projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {projectId && (
                <div className="min-w-0 space-y-2">
                  <Label>Coluna Kanban</Label>
                  {columnsLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando colunas...
                    </div>
                  ) : columns.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Este projeto ainda não possui colunas.
                    </p>
                  ) : (
                    <Select value={columnId} onValueChange={setColumnId}>
                      <SelectTrigger className="w-full max-w-full">
                        <SelectValue placeholder="Selecionar coluna" />
                      </SelectTrigger>
                      <SelectContent>
                        {columns.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={
              submitting ||
              projects.length === 0 ||
              !projectId ||
              columnsLoading ||
              (columns.length > 0 && !columnId)
            }
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Vincular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
